import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Menu, X, LogOut } from 'lucide-react';

export const Navbar = () => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
    setMobileOpen(false);
  };

  const navLinks = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/publicaciones', label: 'Publicaciones' },
    { href: '/events', label: 'Eventos' },
    { href: '/picks', label: 'Picks' },
    { href: '/publishing', label: 'Publicación' },
    { href: '/admin/control', label: 'Control' },
    { href: '/admin/telegram', label: 'Telegram' },
    { href: '/admin/reels', label: 'Reels' },
    { href: '/admin/publisher', label: 'Publisher' },
    { href: '/admin/markets-insights', label: 'Mercados' },
    { href: '/admin/weekly-report', label: 'Resumen' },
    { href: '/admin/stats', label: 'Stats' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 nav-blur" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8">
          <div className="flex items-center justify-between h-14">
            {/* No logo - just navigation */}

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              {user && navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-[#00E599]'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop user menu */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-zinc-400 hover:text-white text-sm"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </Button>
              ) : (
                <Link to="/login">
                  <Button className="btn-primary">Iniciar Sesión</Button>
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 -mr-2 text-zinc-400 hover:text-white active:scale-95 transition-transform"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Nav Menu */}
      <div 
        className={`fixed top-0 right-0 h-full w-64 z-50 bg-zinc-900 border-l border-zinc-800 transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Header */}
          <div className="flex items-center justify-end p-4 border-b border-zinc-800">
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 -mr-2 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Links */}
          <div className="flex-1 overflow-y-auto py-4">
            {user && navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center px-4 py-3.5 text-base font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-[#00E599]/10 text-[#00E599] border-r-2 border-[#00E599]'
                    : 'text-zinc-300 hover:bg-zinc-800/50 active:bg-zinc-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Footer */}
          <div className="p-4 border-t border-zinc-800">
            {user ? (
              <>
                <p className="text-xs text-zinc-500 mb-3 truncate">{user.email}</p>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 h-11"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </Button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                <Button className="w-full btn-primary h-11">Iniciar Sesión</Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
